import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Tags, Upload, Activity } from 'lucide-react';
import ContactList from './ContactList';
import TagManager from './TagManager';
import BusynessAnalytics from '@/components/analytics/BusynessAnalytics';

interface CustomersTabProps {
  businessId: string;
}

/**
 * Main CRM Customers Tab
 *
 * This is the primary container for the CRM system, accessible from the dashboard.
 * It provides:
 * - Contact list with search/filter
 * - Tag management
 * - Import/Export capabilities (future)
 */
const CustomersTab: React.FC<CustomersTabProps> = ({ businessId }) => {
  const [activeSubTab, setActiveSubTab] = useState('contacts');

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <Tags className="h-4 w-4" />
            Tags
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Busyness
          </TabsTrigger>
          <TabsTrigger value="import-export" className="flex items-center gap-2" disabled>
            <Upload className="h-4 w-4" />
            Import/Export
            <span className="ml-1 text-xs text-muted-foreground">(Coming Soon)</span>
          </TabsTrigger>
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <ContactList businessId={businessId} />
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4">
          <TagManager businessId={businessId} />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <BusynessAnalytics businessId={businessId} />
        </TabsContent>

        {/* Import/Export Tab (Placeholder for future) */}
        <TabsContent value="import-export" className="space-y-4">
          <div className="text-center py-12 text-muted-foreground">
            <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Import & Export</h3>
            <p>Bulk import and export functionality coming soon</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomersTab;
